import { ApolloCache } from '@apollo/client'
import {
  DossDataFormat,
  LOGGED_IN_USER_FILTER_ARG,
  LocaleCode,
  formatDateColumnValue,
  formatDateTimeByLocale,
  formatNumberColumnValue,
  getUserVisibleIndexValue,
  getWebPath,
  isDefined,
  isFormulaCellData,
  isFormulaNumberCellData,
  isFormulaStringCellData,
  isFormulaDateCellData,
  isFormulaDateTimeCellData,
  isFormulaType,
  isResolvedIdCellData,
  isResolvedTableCellData,
  isValidArrayInnerType,
  parseSystemColumnFieldId,
} from '@doss/common-all'
import { Box, Stack, Typography } from '@mui/material'
import {
  GridCellModes,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridLogicOperator,
  GridPinnedColumns,
  GridRenderEditCellParams,
  GridSortModel,
  GridTreeNodeWithRender,
  GridValueSetterParams,
  MuiEvent,
} from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import { every, find, first, get, isNil, orderBy, some, uniq } from 'lodash'
import { Dispatch, MouseEvent, SetStateAction } from 'react'
import {
  FiCalendar,
  FiClock,
  FiFile,
  FiFilePlus,
  FiGitBranch,
  FiHash,
  FiList,
  FiUser,
  FiUserPlus,
} from 'react-icons/fi'
import { MdOutlineArrowDropDownCircle } from 'react-icons/md'
import { PiFunctionBold } from 'react-icons/pi'
import { RiCheckboxIndeterminateLine, RiFilter2Fill, RiUserLine } from 'react-icons/ri'
import {
  RxArrowDown,
  RxArrowUp,
  RxClock,
  RxComponentBoolean,
  RxComponentInstance,
  RxCounterClockwiseClock,
  RxLetterCaseCapitalize,
} from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import { Primitive } from 'type-fest'
import {
  ApplicationOrientation,
  BreadcrumbObjectType,
  DataType,
  IndexType,
  SortDirection,
  SystemGeneratedTableColumnType,
  TableSortInput,
} from '~/common/generated/apollo-operations'
import { AllowedArrayInnerTypes } from '~/common/utils/table-column'
import { FormulaPopover } from '~/components/formula/FormulaPopover'
import { LookupProvider } from '~/components/records/lookup/hooks/LookupProvider'
import { RecordDetailTableRow } from '~/components/records/types'
import { ActiveUser } from '~/components/settings/types'
import { DossTableStyles, TableSource } from '~/components/shared/DossTable'
import Link from '~/components/shared/Link'
import { EditTableCellDialog } from '~/components/table-cells/EditTableCellDialog'
import { TableCellComponent } from '~/components/table-cells/TableCell'
import { isNumericColumnType, MetricType } from '~/components/tables/aggregation-functions'
import { AggregationCell, AGGREGATION_ROW_ID } from '~/components/tables/AggregationPinnedRow'
import {
  DossGridFilterModel,
  filterOperatorToBooleanOperator,
  generateFilterInput,
  generateSystemColumnFilterInput,
  isFilterSame,
} from '~/components/tables/filters'
import {
  DetailedTableColumn,
  DetailedTableRow,
  DetailedTableTab,
  DossTableCell,
  DossTableRow,
  TableType,
} from '~/components/tables/types'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
import { UserChip } from '~/components/UserChip'
import { TranslationKeys } from '../generated/i18n.generated'
import { getGridFilterOperators } from './grid-filters'
import { UserPermissionsManager } from './user-permissions-manager'

export const TEXT_LENGTH_LIMIT = 450

export enum ColType {
  String = 'string',
  Number = 'number',
  SingleSelect = 'singleSelect',
  Boolean = 'boolean',
  Date = 'date',
  DateTime = 'dateTime',
}

export type RecordDetailTableRowWithoutButtons = Omit<
  RecordDetailTableRow,
  'currentButtonNodes' | 'processingWorkspaceButton'
>

/**
 * Helper function for generating the column class name given the column index, table type, and
 * optional and extra class names.
 */
export function getColumnClassName(type: TableType, headerId: string, extraClass?: string): string {
  let baseName = `${type}-column-${headerId}`

  if (extraClass) {
    baseName = `${baseName} ${extraClass}`
  }

  return baseName
}

type GetValueFromCellColumn = Pick<
  DetailedTableColumn,
  'id' | 'name' | 'type' | 'indexPrefix' | 'indexSuffix' | 'innerType'
>

function getValueFromCell(
  cell: Pick<DossTableCell, 'data' | 'userVisibleValue'>,
  column: GetValueFromCellColumn,
  includeIdAffixes: boolean,
): string[] | string | number | boolean | null | undefined {
  if (!cell.data) {
    return null
  }

  const data = cell.data as Record<string, unknown>

  if (!isResolvedTableCellData(data)) {
    // Formula cells may be `{ type: FORMULA_* }` with null/absent data (uncomputed) — valid cell
    // data but not "resolved". Return the value or null so we show empty instead of a perpetual
    // loading spinner (FormattedTableCell treats `undefined` as still-denormalizing).
    if (
      isFormulaCellData(data) ||
      isFormulaNumberCellData(data) ||
      isFormulaStringCellData(data) ||
      isFormulaDateCellData(data) ||
      isFormulaDateTimeCellData(data)
    ) {
      return data.data ?? null
    }

    // Return undefined if the data is not resolved yet so that the loading state can be shown
    // in the cell instead. For file urls and lookups, we return the user visible value instead
    // because they may not be resolved in elasticsearch yet
    const allowUserValueTypes = [DataType.FILE_URL, DataType.LOOKUP]
    const isAllowedType = allowUserValueTypes.includes(column.type)
    const isAllowedInnerType = column.innerType && allowUserValueTypes.includes(column.innerType)
    if (cell.userVisibleValue && (isAllowedType || isAllowedInnerType)) {
      return cell.userVisibleValue
    }
    return undefined
  }

  switch (data.type) {
    case DossDataFormat.ARRAY: {
      // Drop nulls (empty lookup targets). Do NOT index-pair the result with
      // cell.lookupCells — LookupArrayTableCell reads cell.data.data directly
      // for the aligned view.
      return data.data.map((value) => (value === null ? '' : value.toString()))
    }
    case DossDataFormat.BOOLEAN:
    case DossDataFormat.DATE:
    case DossDataFormat.DATE_TIME:
    case DossDataFormat.ENUM:
    case DossDataFormat.FILE:
    case DossDataFormat.FORMULA:
    case DossDataFormat.FORMULA_NUMBER:
    case DossDataFormat.FORMULA_STRING:
    case DossDataFormat.FORMULA_DATE:
    case DossDataFormat.FORMULA_DATE_TIME:
    case DossDataFormat.NUMBER:
    case DossDataFormat.STRING:
    case DossDataFormat.USER: {
      return data.data
    }
    case DossDataFormat.ID: {
      if (includeIdAffixes) {
        return getUserVisibleIndexValue(data.data, column.indexPrefix, column.indexSuffix)
      }
      return data.data
    }
    case DossDataFormat.LOOKUP: {
      if (Array.isArray(data.data)) {
        return data.data.map((value) => value.toString())
      }
      return data.data
    }
  }
}

/*
 * Function that will get an Icon depending on the column datatype
 */
export function getIconByColumnType(
  columnType: DataType,
  innerType?: AllowedArrayInnerTypes | null,
  dirty?: boolean,
): JSX.Element {
  switch (columnType) {
    case DataType.ARRAY: {
      if (!innerType || !isValidArrayInnerType(innerType)) {
        // Instead of throwing an error, return a fallback value
        return <FiList />
      }
      switch (innerType) {
        case DataType.ENUM:
          return <FiList />
        case DataType.LOOKUP:
          return <FiGitBranch />
        case DataType.FILE_URL:
          return <FiFilePlus />
        case DataType.USER:
          return <FiUserPlus />
      }
      break
    }
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return <PiFunctionBold />
    case DataType.ID:
      return dirty ? <RxComponentBoolean className="text-blue-8" /> : <RxComponentInstance />
    case DataType.STRING:
      return <RxLetterCaseCapitalize />
    case DataType.NUMBER:
      return <FiHash />
    case DataType.ENUM:
      return <MdOutlineArrowDropDownCircle />
    case DataType.FILE_URL:
      return <FiFile />
    case DataType.USER:
      return <FiUser />
    case DataType.LOOKUP:
      return <FiGitBranch />
    case DataType.DATE:
      return <FiCalendar />
    case DataType.DATE_TIME:
      return <FiClock />
    case DataType.BOOLEAN:
      return <RiCheckboxIndeterminateLine />
  }
}

/**
 * Function will look at column type and inner type and return the complete header icon component
 * that is used in the table header to indicate which field type it is.
 */
export function getHeaderIconComponent(
  columnType: DataType,
  columnInnerType: DataType | null,
  tableColumnHeader: DetailedTableColumn,
  headerIcon: JSX.Element,
  styles: Omit<DossTableStyles, 'datagrid'>,
  handlePopoverOpen: (event: MouseEvent<HTMLButtonElement>) => void,
  setFormulaId: (formulaId: string) => void,
  permissionsManager: UserPermissionsManager,
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: TranslationKeys, options?: Record<string, any>) => string,
): JSX.Element | null {
  const typeToCheck = columnType === DataType.ARRAY ? columnInnerType : columnType

  if (!typeToCheck) {
    return null
  }

  switch (typeToCheck) {
    case DataType.BOOLEAN:
    case DataType.ARRAY:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.ENUM:
    case DataType.NUMBER:
    case DataType.STRING:
      return null
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return (
        <IconButton
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            if (tableColumnHeader.formula) {
              handlePopoverOpen(event)
              setFormulaId(tableColumnHeader.formula.id)
            }
          }}
          size="small"
          sx={{
            ...styles.headerCell.icon,
            ...styles.headerCell.icon.withCursor,
          }}
          variant="true-ghost"
        >
          {headerIcon}
        </IconButton>
      )
    case DataType.ID: {
      let indexType: JSX.Element
      switch (tableColumnHeader.indexType) {
        case IndexType.NUMBER:
          indexType = <Typography>{t('table.header.id.type_number')}</Typography>
          break
        case IndexType.STRING:
          indexType = <Typography>{t('table.header.id.type_string')}</Typography>
          break
        case IndexType.NONE:
          throw new Error(`ID column ${tableColumnHeader.id} has no index type`)
      }

      const autoGenerate = tableColumnHeader.indexAutogenerate ? (
        <Typography>{t('table.header.id.autogenerated')}</Typography>
      ) : null
      const prefix = tableColumnHeader.indexPrefix ? (
        <Typography>
          {t('table.header.id.prefix', {
            prefix: tableColumnHeader.indexPrefix,
          })}
        </Typography>
      ) : null
      const suffix = tableColumnHeader.indexSuffix ? (
        <Typography>
          {t('table.header.id.suffix', {
            suffix: tableColumnHeader.indexSuffix,
          })}
        </Typography>
      ) : null

      return (
        <Tooltip
          arrow
          title={
            <Stack direction="column" spacing={0.5}>
              {t('table.header.id.unique_id')}
              {indexType}
              {autoGenerate}
              {prefix}
              {suffix}
            </Stack>
          }
          type="action"
        >
          <Box
            sx={{
              ...styles.headerCell.icon,
              ...styles.headerCell.icon.withCursor,
            }}
          >
            {headerIcon}
          </Box>
        </Tooltip>
      )
    }

    case DataType.FILE_URL:
      return (
        <Tooltip arrow title={<>{t('table.header.files')}</>} type="action">
          <Box
            sx={{
              ...styles.headerCell.icon,
              ...styles.headerCell.icon.withCursor,
            }}
          >
            {headerIcon}
          </Box>
        </Tooltip>
      )
    case DataType.USER:
      return (
        <Tooltip arrow title={<>{t('table.header.users')}</>} type="action">
          <Box
            sx={{
              ...styles.headerCell.icon,
              ...styles.headerCell.icon.withCursor,
            }}
          >
            {headerIcon}
          </Box>
        </Tooltip>
      )
    case DataType.LOOKUP: {
      const lookupTableId = tableColumnHeader.lookupColumn?.table.id
      const canViewLookupTable = lookupTableId
        ? permissionsManager.canViewTable(lookupTableId)
        : false
      return (
        <Tooltip
          arrow
          title={
            <>
              {t('lookup.linked_by')}
              {canViewLookupTable ? (
                <Link
                  href={getWebPath({
                    objectType: BreadcrumbObjectType.TABLE,
                    objectId: tableColumnHeader.lookupColumn?.table.id ?? '',
                  })}
                >
                  {tableColumnHeader.lookupColumn?.table.name}
                </Link>
              ) : (
                <div>{tableColumnHeader.lookupColumn?.table.name ?? ''}</div>
              )}
            </>
          }
          type="action"
        >
          <Box
            sx={{
              ...styles.headerCell.icon,
              ...styles.headerCell.icon.withCursor,
            }}
          >
            {headerIcon}
          </Box>
        </Tooltip>
      )
    }
  }
}

/**
 * Utility function to get the value of a cell in a row from the GraphQL response.
 */
export function getValueFromRow(
  row: DetailedTableRow | RecordDetailTableRowWithoutButtons,
  column: GetValueFromCellColumn,
  includeIdAffixes: boolean,
): string[] | string | number | boolean | null | undefined {
  const cells = row.cells

  const cell = cells.find((cell) => {
    if (!isDefined(cell.column)) {
      // There is a race condition where when deleting a table column, it may try to get the value
      // for the deleted column's cell before its removed from the cache
      return false
    }
    return cell.column.id === column.id
  })

  if (!cell) {
    // No longer throwing error because there are cases where, after column insertion, the
    // cell may not be found right away
    return null
  }

  return getValueFromCell(cell, column, includeIdAffixes)
}

/**
 * Cache for column width calculations to avoid redundant DOM measurements.
 * Key format: `${tableType}-${headerId}-${rowCount}`
 *
 * Cleared when:
 * - Table type changes (different tableType in key)
 * - Column ID changes (different headerId in key)
 * - Number of rows changes significantly (different rowCount bucket in key)
 */
const columnWidthCache = new Map<string, number>()

function getColumnWidthCacheKey(tableType: TableType, tableId: string, headerId?: string): string {
  if (headerId) {
    return `${tableType}-${tableId}-${headerId}`
  }
  return `${tableType}-${tableId}`
}

/**
 * Clears cache entries for a specific table type.
 * Call when switching tables or table type changes.
 */
export function clearColumnWidthCache(tableType: TableType, tableId: string): void {
  // Clear only entries for this table type
  const keysToDelete: string[] = []
  columnWidthCache.forEach((_, key) => {
    if (key.startsWith(getColumnWidthCacheKey(tableType, tableId))) {
      keysToDelete.push(key)
    }
  })
  keysToDelete.forEach((key) => columnWidthCache.delete(key))
}

/**
 * Utility function to get the max width of a column in a table
 * Inspired by the comment https://github.com/mui/mui-x/issues/1241#issuecomment-1229443310
 * from a thread in the MUI repo where the team has not addressed an officially supported way to
 * build the autofit functionality that we need.
 */
export function maxColumnWidth(
  colIndex: number,
  textLimit: number,
  tableType: TableType,
  tableId: string,
  headerId: string,
): number {
  // Check cache first
  const columnClassName = getColumnClassName(tableType, headerId)
  const cacheKey = getColumnWidthCacheKey(tableType, tableId, headerId)

  const cachedWidth = columnWidthCache.get(cacheKey)
  if (cachedWidth !== undefined) {
    return cachedWidth
  }

  // Cache miss - perform DOM measurement
  const invisibleContainer = document.createElement('div')
  invisibleContainer.style.visibility = 'hidden'
  invisibleContainer.style.zIndex = '-9999999999'
  invisibleContainer.style.position = 'absolute'
  invisibleContainer.style.fontSize = '14px'
  invisibleContainer.style.top = '0'
  invisibleContainer.style.left = '0'
  document.body.append(invisibleContainer)

  const widths: number[] = []

  /**
   * With this function we iterate over every cell in the selected row and create an invisible
   * cell to get the width of the content. We then append the widths to an array which is used to
   * find the max width of what is present in the column.
   *
   * If the content is marked with autofit-hover-content, then it means it is something that appears
   * on hover which we do not want to include in our resizing because then every time you hover
   * over a row and new component appears then the widths will change.
   */
  const cellContents = document.querySelectorAll(`.${columnClassName}`)
  cellContents.forEach((cell) => {
    const cloneCell = cell.cloneNode(true) as HTMLElement
    const hoverContents = cloneCell.querySelectorAll('.autofit-hover-content')
    hoverContents.forEach((hoverContent) => {
      hoverContent.remove()
    })
    const invisibleCell = document.createElement('div')
    invisibleCell.innerHTML = cloneCell.innerHTML
    invisibleCell.style.cssText = 'width: max-content; max-width: none; min-width: none;'
    invisibleContainer.append(invisibleCell)

    widths.push(Math.ceil(invisibleCell.clientWidth + 1))
  })

  if (widths.length === 0) {
    invisibleContainer.remove()
    return 0 // DOM not ready yet — don't cache
  }

  widths.forEach((cellWidth, index) => {
    if (cellWidth > textLimit) {
      // If content width exceeds the limit, set the max width as the limits
      widths[index] = textLimit
    }
  })

  let max = Math.max(...widths)
  max += 24 // Add 24px to account additional padding

  // If the column is the first one, add the width of the Open button
  if (colIndex === 0) {
    /**
     * Roughly 2x the size of the icon + margin/padding on the component so that there's a good
     * amount of space but not excessive.
     * */
    const buttonWidth = 64
    max += buttonWidth
  }

  // Set the smallest possible size to 30px
  if (max !== 0 && max < 30) {
    max = 30
  }

  invisibleContainer.remove()

  // Cache the result before returning
  columnWidthCache.set(cacheKey, max)

  return max
}

/**
 * Get the ColType for a given lookup column.
 */
export function getColTypeForLookupColumn(
  lookupColumn: NonNullable<DetailedTableColumn['lookupColumn']>,
): ColType {
  switch (lookupColumn.type) {
    case DataType.STRING:
    case DataType.FILE_URL:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return ColType.String
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
    case DataType.NUMBER:
      return ColType.Number

    case DataType.FORMULA_BOOLEAN:
    case DataType.BOOLEAN:
      return ColType.Boolean
    case DataType.USER:
    case DataType.ENUM:
      return ColType.SingleSelect
    case DataType.ID: {
      switch (lookupColumn.indexType) {
        case IndexType.NONE:
          throw new Error(`ID column ${lookupColumn.id} has no index type`)
        case IndexType.NUMBER:
          return ColType.Number
        case IndexType.STRING:
          return ColType.String
      }
      break
    }
    case DataType.LOOKUP:
      // This is not supported but does exist in production for a nested lookup of string value,
      // so we treat it as a string lookup
      return ColType.String
    case DataType.ARRAY:
      // Lookups of arrays are not supported, so default to string
      return ColType.String
  }
}

/**
 * Get the ColType for a given column based on the column's type. For lookup columns, the type is
 * determined by the lookup column's type.
 */
function getColTypeForColumn(column: DetailedTableColumn): ColType {
  switch (column.type) {
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
      return ColType.Number
    case DataType.FORMULA_STRING:
      return ColType.String
    case DataType.FORMULA_DATE:
      return ColType.Date
    case DataType.FORMULA_DATE_TIME:
      return ColType.DateTime
    case DataType.FORMULA_BOOLEAN:
      return ColType.Boolean
    case DataType.ID: {
      switch (column.indexType) {
        case IndexType.NONE:
          throw new Error(`ID column ${column.id} has no index type`)
        case IndexType.NUMBER:
          return ColType.Number
        case IndexType.STRING:
          return ColType.String
      }
      break
    }
    case DataType.STRING:
    case DataType.FILE_URL:
      return ColType.String
    case DataType.LOOKUP: {
      if (!column.lookupColumn) {
        throw new Error(`Column ${column.id} expected to have lookup column`)
      }
      return getColTypeForLookupColumn(column.lookupColumn)
    }
    case DataType.USER:
    case DataType.ENUM:
      return ColType.SingleSelect
    case DataType.NUMBER:
      return ColType.Number
    case DataType.BOOLEAN:
      return ColType.Boolean
    case DataType.DATE:
      return ColType.Date
    case DataType.DATE_TIME:
      return ColType.DateTime
    case DataType.ARRAY: {
      const innerType = column.innerType
      if (!isValidArrayInnerType(innerType)) {
        throw new Error(`Invalid inner type ${innerType} for array column ${column.id}`)
      }
      switch (innerType) {
        case DataType.USER:
        case DataType.ENUM:
          return ColType.SingleSelect
        case DataType.LOOKUP: {
          if (!column.lookupColumn) {
            throw new Error(`Array column ${column.id} expected to have lookup column`)
          }
          return getColTypeForLookupColumn(column.lookupColumn)
        }
        case DataType.FILE_URL:
          return ColType.String
      }
    }
  }
}

/**
 * Get the dropdown options for a column of type USER or ARRAY with inner type USER
 */
export function getUserFilterOptions(
  activeUsers: ActiveUser[] | undefined,
): Array<{ value: string; label: string }> {
  const users =
    activeUsers?.map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
    })) ?? []

  const loggedInUserOption = {
    value: LOGGED_IN_USER_FILTER_ARG,
    label: LOGGED_IN_USER_FILTER_ARG,
  }

  return [loggedInUserOption, ...users.sort((a, b) => a.label.localeCompare(b.label))]
}

/**
 * Get the value options for a column if it matches any of the following conditions:
 * - The column is of type ENUM or ARRAY with inner type ENUM
 * - The column is of type LOOKUP and the lookup column is of type ENUM or ARRAY
 *   with inner type ENUM
 * - The column is of type USER or ARRAY with inner type USER
 * - The column is of type LOOKUP and the lookup column is of type USER or ARRAY
 *   with inner type USER
 */
function getValueOptions(
  tableColumn: DetailedTableColumn,
  activeUsers: ActiveUser[] | undefined,
): string[] | { value: string; label: string }[] | undefined {
  let valueOptions: string[] | { value: string; label: string }[] | undefined
  if (tableColumn.type === DataType.ENUM || tableColumn.innerType === DataType.ENUM) {
    valueOptions = tableColumn.enumOptions ?? []
  }

  if (tableColumn.type === DataType.USER || tableColumn.innerType === DataType.USER) {
    valueOptions = getUserFilterOptions(activeUsers)
  }

  if (tableColumn.type === DataType.LOOKUP) {
    // For lookup columns, use resolvedSourceType and resolvedEnumOptions to handle multi-level lookups
    const resolvedType = tableColumn.resolvedSourceType

    if (resolvedType === DataType.ENUM) {
      valueOptions = tableColumn.resolvedEnumOptions ?? []
    }

    if (resolvedType === DataType.USER) {
      valueOptions = getUserFilterOptions(activeUsers)
    }
  }
  return valueOptions
}

export const LAST_COLUMN_CLASSNAME = 'last-column'

interface GetGridColDefsParams {
  tableId: string
  tableColumnHeaders: DetailedTableColumn[]
  tableType: TableType
  styles: Omit<DossTableStyles, 'datagrid'>
  maxColWidths: number[]
  storedColWidths: Record<string, number>
  sorts?: TableSortInput[] | null
  handlePopoverOpen: (event: MouseEvent<HTMLButtonElement>) => void
  setFormulaId: (formulaId: string) => void
  columnVisibilityModel: GridColumnVisibilityModel
  readOnly: boolean
  sortedFrozenColumns: GridPinnedColumns['left']
  locale: LocaleCode
  activeUsers: ActiveUser[] | undefined
  includeSystemGeneratedColumns?: boolean
  setRowsData: Dispatch<SetStateAction<DetailedTableRow[]>>
  permissionsManager: UserPermissionsManager
  t: (key: TranslationKeys) => string
  orientation: ApplicationOrientation
  tableSource: TableSource
  tableName: string
  filterModel?: DossGridFilterModel
  onFilterIconClick?: (columnId: string) => void
  tableTabId?: string
  // Aggregation row support
  selectedTab?: DetailedTableTab
  showAggregationRow?: boolean
  columnMetrics?: Record<string, MetricType>
  setColumnMetrics?: React.Dispatch<React.SetStateAction<Record<string, MetricType>>>
  /**
   * For child tables: the parent row ID to scope aggregation to children of that parent.
   */
  parentRowId?: string
  /**
   * For backlink tables: the column containing the link to the source table.
   * Must be provided together with backlinkRowId.
   */
  backlinkColumnId?: string
  /**
   * For backlink tables: the source row ID to scope aggregation to rows linked to that row.
   * Must be provided together with backlinkColumnId.
   */
  backlinkRowId?: string
  /**
   * Optional search string to filter aggregation results.
   */
  searchString?: string
}

/**
 * Utility function to get the DataGrid.GridColDef for a table from the GraphQL response.
 */
export function getGridColDefs({
  tableId,
  tableName,
  tableColumnHeaders,
  tableType,
  styles,
  maxColWidths,
  storedColWidths,
  sorts,
  handlePopoverOpen,
  setFormulaId,
  columnVisibilityModel,
  readOnly,
  sortedFrozenColumns,
  locale,
  activeUsers,
  setRowsData,
  includeSystemGeneratedColumns = true,
  permissionsManager,
  t,
  orientation = ApplicationOrientation.LEFT_TO_RIGHT,
  tableSource,
  filterModel,
  onFilterIconClick,
  tableTabId,
  selectedTab,
  showAggregationRow = false,
  columnMetrics,
  setColumnMetrics,
  parentRowId,
  backlinkColumnId,
  backlinkRowId,
  searchString,
}: GetGridColDefsParams): GridColDef[] {
  const sortedTableColumns = orderBy(
    tableColumnHeaders,
    (tableColumnHeader) => tableColumnHeader.order,
    'asc',
  )

  const firstVisibleColumn = find(
    sortedTableColumns,
    (tableColumnHeader) => columnVisibilityModel[tableColumnHeader.id],
  )
  const firstVisibleFrozenColumnId = find(
    sortedFrozenColumns,
    (columnId) => columnVisibilityModel[columnId],
  )
  // The first visible frozen column overrides the first visible column out of all columns
  const firstVisibleColumnId = firstVisibleFrozenColumnId ?? firstVisibleColumn?.id

  const canConfigureTableTabs = permissionsManager.canConfigureTableTabs(tableId)

  // Setting the text length limit for a given column before we decide to wrap it
  const cols: GridColDef[] = sortedTableColumns.map((tableColumnHeader, index) => {
    // Get the header icon for the given column type
    const headerIcon = getIconByColumnType(
      tableColumnHeader.type,
      tableColumnHeader.innerType as AllowedArrayInnerTypes,
    )
    const headerIconComponent = getHeaderIconComponent(
      tableColumnHeader.type,
      tableColumnHeader.innerType,
      tableColumnHeader,
      headerIcon,
      styles,
      handlePopoverOpen,
      setFormulaId,
      permissionsManager,
      t,
    )

    const maxColWidth = maxColWidths[index]
    const colType = getColTypeForColumn(tableColumnHeader)
    // Get the MUI filter operators based on the column's type
    const filterOperators = getGridFilterOperators(
      { ...tableColumnHeader, table: { id: tableId, name: tableName } },
      { activeUsers, includeDuplicateOperator: true },
    )

    // Get the value options for enum and user type columns
    const valueOptions = getValueOptions(tableColumnHeader, activeUsers)

    // Set if the column has a description
    const hasDescription =
      tableColumnHeader.description !== null && tableColumnHeader.description !== ''

    // Get sort direction if sort is being applied
    const sort = find(sorts, (sort) => sort.columnId === tableColumnHeader.id)
    const sortDirection = sort?.direction ?? null

    const hasPermissionToEditCell = permissionsManager.canEditCell(tableId, tableColumnHeader.id)

    const width = storedColWidths[tableColumnHeader.id] || maxColWidth

    // Count filters for this column
    const columnFilters =
      filterModel?.items.filter(
        (filter) => filter.field === tableColumnHeader.id && filter.tableId === tableId,
      ) ?? []
    const filterCount = columnFilters.length

    return {
      field: tableColumnHeader.id,
      headerName: tableColumnHeader.name,
      disableReorder: true,
      editable: !readOnly && !tableColumnHeader.isReadOnly && hasPermissionToEditCell,
      width,
      type: colType,
      sortable: false, // Remove default MUI sorting features
      filterable: true,
      filterOperators,
      disableColumnMenu: !canConfigureTableTabs,
      valueOptions,
      headerAlign: orientation === ApplicationOrientation.LEFT_TO_RIGHT ? 'left' : 'right',

      renderHeader: () => {
        return (
          <Box
            className="non-hover-content"
            sx={{
              ...styles.headerCell,
              ...styles.headerCell.leftIndent(index),
            }}
          >
            {headerIconComponent}
            <Tooltip
              arrow
              offset={-20}
              paddingSetting={hasDescription ? 'none' : 'default'}
              title={
                <Stack spacing={0.5}>
                  <Typography sx={styles.headerDescriptionTitle(hasDescription)} variant="body2">
                    {tableColumnHeader.name}
                  </Typography>
                  {tableColumnHeader.description && (
                    <Typography sx={styles.headerDescriptionText} variant="body1">
                      {tableColumnHeader.description}
                    </Typography>
                  )}
                </Stack>
              }
              type="primary"
            >
              <Box sx={styles.headerCell.name}>{tableColumnHeader.name}</Box>
            </Tooltip>
            {sortDirection && (
              <Box sx={styles.headerCell.sortIcon}>
                {sortDirection === SortDirection.ASC ? <RxArrowUp /> : <RxArrowDown />}
              </Box>
            )}
            {filterCount > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  onFilterIconClick?.(tableColumnHeader.id)
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  ml: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.7,
                  },
                }}
              >
                <RiFilter2Fill size={16} style={{ color: 'inherit' }} />
                <Box
                  sx={{
                    visibility: filterCount > 1 ? 'visible' : 'hidden',
                    minWidth: 14,
                    height: 14,
                    color: 'primary.main',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: -1, // Move the badge up slightly
                    marginInlineStart: -1,
                  }}
                >
                  {filterCount}
                </Box>
              </Box>
            )}
          </Box>
        )
      },
      /**
       * Derives the cell value for the grid (filters, sorting, editing, clipboard/export baseline).
       * It does not drive what is painted — `renderCell` does. For copy/CSV, MUI uses `valueFormatter`
       * when `unstable_ignoreValueFormatterDuringExport` does not skip that surface; otherwise the raw
       * value from here is stringified.
       */
      valueGetter: (params: { row: DetailedTableRow }) => {
        const isDateTypeColumn =
          tableColumnHeader.type === DataType.DATE || tableColumnHeader.type === DataType.DATE_TIME
        const value = getValueFromRow(params.row, tableColumnHeader, true)
        if (isDateTypeColumn) {
          // Date strings must be converted to Date() objects in order to use Mui's date filters
          return value && typeof value === 'string' ? dayjs(value).toDate() : null
        } else {
          return value
        }
      },
      ...(colType === ColType.Number
        ? {
            valueFormatter: ({ value }: { value: unknown }) =>
              formatNumberColumnValue(value, tableColumnHeader, locale),
          }
        : {}),
      ...(colType === ColType.Date || colType === ColType.DateTime
        ? {
            valueFormatter: ({ value }: { value: unknown }) =>
              formatDateColumnValue(
                value,
                {
                  formatPreset: tableColumnHeader.formatPreset,
                  formatOptions: tableColumnHeader.formatOptions,
                  isDateTime:
                    tableColumnHeader.type === DataType.DATE_TIME ||
                    tableColumnHeader.type === DataType.FORMULA_DATE_TIME,
                },
                locale,
              ),
          }
        : {}),
      /**
       * We use this column class overwriting to address the column width indexes being shared
       * between the parent and child tables. In order to separate then (and not rely on the
       * aria-colindex generated by MUI we use the column class to differentiate between the two.
       *
       * There is another set of logic we apply so we can add whether it is the last column,
       * this way we know how to apply the borders.
       */
      headerClassName: getColumnClassName(tableType, tableColumnHeader.id),
      cellClassName: getColumnClassName(tableType, tableColumnHeader.id),
      renderCell: (params: { row: DetailedTableRow }) => {
        // Handle aggregation row - render aggregation cell for numeric columns
        if (showAggregationRow && params.row.id === AGGREGATION_ROW_ID) {
          // Use resolvedSourceType for lookups to handle multi-level lookup chains to numeric columns
          const effectiveType =
            tableColumnHeader.type === DataType.LOOKUP
              ? tableColumnHeader.resolvedSourceType
              : tableColumnHeader.type
          if (isNumericColumnType(effectiveType)) {
            return (
              <AggregationCell
                backlinkColumnId={backlinkColumnId}
                backlinkRowId={backlinkRowId}
                columnId={tableColumnHeader.id}
                columnType={effectiveType}
                filterModel={
                  filterModel ?? {
                    items: [],
                    logicOperator: GridLogicOperator.And,
                  }
                }
                formatOptions={tableColumnHeader.formatOptions}
                formatPreset={tableColumnHeader.formatPreset}
                onMetricChange={
                  setColumnMetrics
                    ? (metric) =>
                        setColumnMetrics((prev) => ({
                          ...prev,
                          [tableColumnHeader.id]: metric,
                        }))
                    : undefined
                }
                parentRowId={parentRowId}
                searchString={searchString}
                selectedMetric={columnMetrics?.[tableColumnHeader.id]}
                selectedTab={selectedTab}
                tableColumns={tableColumnHeaders}
                tableTabId={tableTabId}
              />
            )
          }
          // Non-numeric columns show empty cell in aggregation row
          return null
        }

        const value = getValueFromRow(params.row, tableColumnHeader, true)

        const cell = params.row.cells.find((cell) => {
          if (!isDefined(cell.column)) {
            // There is a race condition where when deleting a table column, it may try to get the value
            // for the deleted column's cell before its removed from the cache
            return false
          }
          return cell.column.id === tableColumnHeader.id
        })

        if (!cell) {
          return null
        }

        return (
          <LookupProvider
            columns={tableColumnHeader.lookupColumn ? [tableColumnHeader.lookupColumn] : []}
            isUnauth={false}
          >
            <Box sx={tableColumnHeader.isReadOnly ? styles.readOnlyCell : {}}>
              <TableCellComponent
                activeUsers={activeUsers ?? []}
                cell={{
                  ...cell,
                  column: tableColumnHeader,
                }}
                isFirstColumn={firstVisibleColumnId === tableColumnHeader.id}
                maxColWidth={maxColWidth}
                readOnly={readOnly}
                rowId={params.row.id}
                tableColumnHeader={tableColumnHeader}
                tableSource={tableSource}
                tableType={tableType}
                value={value}
              />
            </Box>
          </LookupProvider>
        )
      },
      /**
       * We use this valueSetter to update the cell data when a cell is edited. This is a necessary
       * step to ensure that the data is updated in the datagrid itself rather than just on the
       * backend.
       */
      valueSetter: (params: GridValueSetterParams<DossTableRow, Primitive | DossTableCell>) => {
        const updatedRow = params.row
        const updatedCell = params.value

        // Ignore null/undefined values
        if (isNil(updatedCell)) {
          return updatedRow
        }

        // Ignore primitive values that aren't table cell objects
        if (typeof updatedCell !== 'object' || !('id' in updatedCell)) {
          return updatedRow
        }

        updatedRow.cells = updatedRow.cells.map((cell) => {
          if (isDefined(cell.column) && cell.column.id === tableColumnHeader.id) {
            return updatedCell
          }
          return cell
        })
        return updatedRow
      },
      renderEditCell: (
        params: GridRenderEditCellParams<
          DossTableRow,
          DossTableCell,
          DetailedTableColumn,
          GridTreeNodeWithRender
        >,
      ) => {
        // Don't render edit cell for aggregation row
        if (showAggregationRow && params.row.id === AGGREGATION_ROW_ID) {
          return null
        }

        const currentCellElement = params.api.getCellElement(params.row.id, tableColumnHeader.id)
        const currentColumnElement = params.api.getColumnHeaderElement(tableColumnHeader.id)
        const value = getValueFromRow(params.row, tableColumnHeader, false)
        const cell = params.row.cells.find((cell) => {
          if (!isDefined(cell.column)) {
            return false
          }
          return cell.column.id === tableColumnHeader.id
        })

        // If the cell is editable but still loading, display the edit dialog but with a loading
        // spinner
        if (!cell && !tableColumnHeader.isReadOnly && hasPermissionToEditCell) {
          return (
            <EditTableCellDialog
              anchorEl={currentCellElement}
              cell={cell}
              maxColWidth={maxColWidth}
              params={params}
              row={params.row}
              setRowsData={setRowsData}
              tableId={tableId}
              value={value}
            />
          )
        }

        if (!cell) {
          return null
        }

        return (
          <If condition={isFormulaType(tableColumnHeader.type)}>
            <Then>
              <FormulaPopover
                align="top"
                anchorEl={currentColumnElement}
                formulaId={tableColumnHeader.formula?.id}
                onClose={() =>
                  params.api.stopCellEditMode({
                    id: params.id,
                    field: params.field,
                  })
                }
                tableId={tableId}
              />
              {/* Render the normal table cell component for formulas since they can't be edited */}
              <TableCellComponent
                activeUsers={activeUsers ?? []}
                cell={{
                  ...cell,
                  column: tableColumnHeader,
                }}
                isFirstColumn={firstVisibleColumnId === tableColumnHeader.id}
                maxColWidth={maxColWidth}
                readOnly={readOnly}
                rowId={params.row.id}
                tableColumnHeader={tableColumnHeader}
                tableSource={tableSource}
                tableType={tableType}
                value={value}
              />
            </Then>
            <Else>
              <If
                condition={isResolvedIdCellData(cell.data) && tableColumnHeader.indexAutogenerate}
              >
                <Then>
                  {/* Render the normal table cell component with a tooltip for autogenerated Ids
                   since they can't be edited */}
                  <TableCellComponent
                    activeUsers={activeUsers ?? []}
                    cell={{
                      ...cell,
                      column: tableColumnHeader,
                    }}
                    isFirstColumn={firstVisibleColumnId === tableColumnHeader.id}
                    maxColWidth={maxColWidth}
                    readOnly={readOnly}
                    rowId={params.row.id}
                    showAutogeneratedIdTooltip
                    tableColumnHeader={tableColumnHeader}
                    tableSource={tableSource}
                    tableType={tableType}
                    value={getValueFromRow(params.row, tableColumnHeader, true)}
                  />
                </Then>
                <Else>
                  <EditTableCellDialog
                    anchorEl={currentCellElement}
                    cell={{
                      ...cell,
                      column: tableColumnHeader,
                    }}
                    maxColWidth={maxColWidth}
                    params={params}
                    row={params.row}
                    setRowsData={setRowsData}
                    tableId={tableId}
                    value={value}
                  />
                </Else>
              </If>
            </Else>
          </If>
        )
      },
    }
  })

  if (!includeSystemGeneratedColumns) {
    return cols
  }

  return [
    ...cols,
    {
      // System generated - CREATED AT
      disableColumnMenu: !canConfigureTableTabs,
      field: SystemGeneratedTableColumnType.CREATED_AT,
      width: get(
        storedColWidths,
        SystemGeneratedTableColumnType.CREATED_AT,
        maxColumnWidth(
          tableColumnHeaders.length,
          TEXT_LENGTH_LIMIT,
          tableType,
          tableId,
          SystemGeneratedTableColumnType.CREATED_AT,
        ),
      ),
      headerName: 'Created At',
      editable: false,
      type: ColType.String,
      sortable: false, // Remove default MUI sorting features
      filterable: true,
      filterOperators: getGridFilterOperators(
        {
          id: SystemGeneratedTableColumnType.CREATED_AT,
          order: 0,
          name: 'Created at',
          type: DataType.DATE_TIME,
          innerType: null,
          indexType: IndexType.NONE,
          resolvedSourceType: DataType.DATE_TIME,
          table: { id: tableId, name: tableName },
          lookupColumn: null,
        },
        { activeUsers },
      ),
      headerAlign: 'left',
      headerClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.CREATED_AT),
      cellClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.CREATED_AT),
      renderHeader: () => {
        const columnFilters =
          filterModel?.items.filter(
            (filter) =>
              filter.field === SystemGeneratedTableColumnType.CREATED_AT &&
              filter.tableId === tableId,
          ) ?? []
        const filterCount = columnFilters.length
        const sort = find(
          sorts,
          (sort) => sort.columnId === SystemGeneratedTableColumnType.CREATED_AT,
        )
        const sortDirection = sort?.direction
        return (
          <Box
            className="non-hover-content"
            sx={{
              ...styles.headerCell,
              ...styles.headerCell.leftIndent(tableColumnHeaders.length + 1),
            }}
          >
            <Box sx={styles.headerCell.icon}>
              <RxClock />
            </Box>
            <Box sx={styles.headerCell.secondaryName}>Created at</Box>
            {sortDirection && (
              <Box sx={styles.headerCell.sortIcon}>
                {sortDirection === SortDirection.ASC ? <RxArrowUp /> : <RxArrowDown />}
              </Box>
            )}
            {filterCount > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  onFilterIconClick?.(SystemGeneratedTableColumnType.CREATED_AT)
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  ml: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                <RiFilter2Fill size={16} style={{ color: 'inherit' }} />
                <Box
                  sx={{
                    visibility: filterCount > 1 ? 'visible' : 'hidden',
                    minWidth: 14,
                    height: 14,
                    color: 'primary.main',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: -1,
                    marginInlineStart: -1,
                  }}
                >
                  {filterCount}
                </Box>
              </Box>
            )}
          </Box>
        )
      },
      valueGetter: (params: { row: DetailedTableRow }) =>
        formatDateTimeByLocale(params.row.createdAt, locale, undefined, true),
      renderCell: (params: { row: DetailedTableRow }) => {
        // Don't render for aggregation row
        if (showAggregationRow && params.row.id === AGGREGATION_ROW_ID) {
          return null
        }
        // Return undefined to use default rendering from valueGetter
        return undefined
      },
    },
    {
      // System generated - CREATED BY
      disableColumnMenu: !canConfigureTableTabs,
      field: SystemGeneratedTableColumnType.CREATED_BY,
      width: get(
        storedColWidths,
        SystemGeneratedTableColumnType.CREATED_BY,
        maxColumnWidth(
          tableColumnHeaders.length,
          TEXT_LENGTH_LIMIT,
          tableType,
          tableId,
          SystemGeneratedTableColumnType.CREATED_BY,
        ),
      ),
      headerName: 'Created by',
      editable: false,
      filterable: true,
      filterOperators: getGridFilterOperators(
        {
          id: SystemGeneratedTableColumnType.CREATED_BY,
          order: 0,
          name: 'Created by',
          type: DataType.USER,
          innerType: null,
          indexType: IndexType.NONE,
          resolvedSourceType: DataType.USER,
          table: { id: tableId, name: tableName },
          lookupColumn: null,
        },
        { activeUsers },
      ),
      type: ColType.String,
      sortable: false, // Remove default MUI sorting features
      headerAlign: 'left',
      headerClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.CREATED_BY),
      cellClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.CREATED_BY),
      renderHeader: () => {
        const columnFilters =
          filterModel?.items.filter(
            (filter) =>
              filter.field === SystemGeneratedTableColumnType.CREATED_BY &&
              filter.tableId === tableId,
          ) ?? []
        const filterCount = columnFilters.length
        const sort = find(sorts, (s) => s.columnId === SystemGeneratedTableColumnType.CREATED_BY)
        const sortDirection = sort?.direction
        return (
          <Box
            className="non-hover-content"
            sx={{
              ...styles.headerCell,
              ...styles.headerCell.leftIndent(tableColumnHeaders.length + 1),
            }}
          >
            <Box sx={styles.headerCell.icon}>
              <RiUserLine />
            </Box>
            <Box sx={styles.headerCell.secondaryName}>Created by</Box>
            {sortDirection && (
              <Box sx={styles.headerCell.sortIcon}>
                {sortDirection === SortDirection.ASC ? <RxArrowUp /> : <RxArrowDown />}
              </Box>
            )}
            {filterCount > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  onFilterIconClick?.(SystemGeneratedTableColumnType.CREATED_BY)
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  ml: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                <RiFilter2Fill size={16} style={{ color: 'inherit' }} />
                <Box
                  sx={{
                    visibility: filterCount > 1 ? 'visible' : 'hidden',
                    minWidth: 14,
                    height: 14,
                    color: 'primary.main',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: -1,
                    marginInlineStart: -1,
                  }}
                >
                  {filterCount}
                </Box>
              </Box>
            )}
          </Box>
        )
      },
      valueGetter: (params: { row: DetailedTableRow }) => {
        return params.row.createdBy
          ? `${params.row.createdBy.firstName} ${params.row.createdBy.lastName}`
          : ''
      },
      renderCell: (params: { row: DetailedTableRow }) => {
        // Don't render for aggregation row
        if (showAggregationRow && params.row.id === AGGREGATION_ROW_ID) {
          return null
        }

        if (!params.row.createdBy) {
          return null
        }

        const user = params.row.createdBy

        return <UserChip user={user} />
      },
    },
    {
      // System generated - UPDATED AT
      disableColumnMenu: !canConfigureTableTabs,
      field: SystemGeneratedTableColumnType.UPDATED_AT,
      width: get(
        storedColWidths,
        SystemGeneratedTableColumnType.UPDATED_AT,
        maxColumnWidth(
          tableColumnHeaders.length,
          TEXT_LENGTH_LIMIT,
          tableType,
          tableId,
          SystemGeneratedTableColumnType.UPDATED_AT,
        ),
      ),
      headerName: 'Updated At',
      editable: false,
      type: ColType.String,
      sortable: false, // Remove default MUI sorting features
      filterable: true,
      filterOperators: getGridFilterOperators(
        {
          id: SystemGeneratedTableColumnType.UPDATED_AT,
          order: 0,
          name: 'Updated at',
          type: DataType.DATE_TIME,
          innerType: null,
          indexType: IndexType.NONE,
          resolvedSourceType: DataType.DATE_TIME,
          table: { id: tableId, name: tableName },
          lookupColumn: null,
        },
        { activeUsers },
      ),
      headerAlign: 'left',
      headerClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.UPDATED_AT),
      cellClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.UPDATED_AT),
      renderHeader: () => {
        const columnFilters =
          filterModel?.items.filter(
            (filter) =>
              filter.field === SystemGeneratedTableColumnType.UPDATED_AT &&
              filter.tableId === tableId,
          ) ?? []
        const filterCount = columnFilters.length
        const sort = find(sorts, (s) => s.columnId === SystemGeneratedTableColumnType.UPDATED_AT)
        const sortDirection = sort?.direction
        return (
          <Box
            className="non-hover-content"
            sx={{
              ...styles.headerCell,
              ...styles.headerCell.leftIndent(tableColumnHeaders.length + 2),
            }}
          >
            <Box sx={styles.headerCell.icon}>
              <RxCounterClockwiseClock />
            </Box>
            <Box sx={styles.headerCell.secondaryName}>Updated at</Box>
            {sortDirection && (
              <Box sx={styles.headerCell.sortIcon}>
                {sortDirection === SortDirection.ASC ? <RxArrowUp /> : <RxArrowDown />}
              </Box>
            )}
            {filterCount > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  onFilterIconClick?.(SystemGeneratedTableColumnType.UPDATED_AT)
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  ml: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                <RiFilter2Fill size={16} style={{ color: 'inherit' }} />
                <Box
                  sx={{
                    visibility: filterCount > 1 ? 'visible' : 'hidden',
                    minWidth: 14,
                    height: 14,
                    color: 'primary.main',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: -1,
                    marginInlineStart: -1,
                  }}
                >
                  {filterCount}
                </Box>
              </Box>
            )}
          </Box>
        )
      },
      valueGetter: (params: { row: DetailedTableRow }) => {
        // Get the most recent updatedAt for the rows cells and compare it to the row's updatedAt
        // date and select the one which is most recent
        const rowCellsUpdatedAt = params.row.cells.map((cell) => dayjs(cell.updatedAt))
        const sortedRowCellsUpdatedAt = orderBy(
          rowCellsUpdatedAt,
          (updatedAt) => updatedAt.valueOf(),
          'desc',
        )
        const cellsLatestUpdatedAt = first(sortedRowCellsUpdatedAt)
        const rowUpdatedAt = dayjs(params.row.updatedAt)
        return cellsLatestUpdatedAt?.isAfter(rowUpdatedAt)
          ? formatDateTimeByLocale(cellsLatestUpdatedAt, locale, undefined, true)
          : formatDateTimeByLocale(rowUpdatedAt, locale, undefined, true)
      },
      renderCell: (params: { row: DetailedTableRow }) => {
        // Don't render for aggregation row
        if (showAggregationRow && params.row.id === AGGREGATION_ROW_ID) {
          return null
        }
        // Return undefined to use default rendering from valueGetter
        return undefined
      },
    },
    {
      // System generated - UPDATED BY
      disableColumnMenu: !canConfigureTableTabs,
      field: SystemGeneratedTableColumnType.UPDATED_BY,
      width: get(
        storedColWidths,
        SystemGeneratedTableColumnType.UPDATED_BY,
        maxColumnWidth(
          tableColumnHeaders.length,
          TEXT_LENGTH_LIMIT,
          tableType,
          tableId,
          SystemGeneratedTableColumnType.UPDATED_BY,
        ),
      ),
      headerName: 'Updated by',
      editable: false,
      filterable: true,
      filterOperators: getGridFilterOperators(
        {
          id: SystemGeneratedTableColumnType.UPDATED_BY,
          order: 0,
          name: 'Updated by',
          type: DataType.USER,
          innerType: null,
          indexType: IndexType.NONE,
          resolvedSourceType: DataType.USER,
          table: { id: tableId, name: tableName },
          lookupColumn: null,
        },
        { activeUsers },
      ),
      type: ColType.String,
      sortable: false, // Remove default MUI sorting features
      headerAlign: 'left',
      headerClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.UPDATED_BY),
      cellClassName: getColumnClassName(tableType, SystemGeneratedTableColumnType.UPDATED_BY),
      renderHeader: () => {
        const columnFilters =
          filterModel?.items.filter(
            (filter) =>
              filter.field === SystemGeneratedTableColumnType.UPDATED_BY &&
              filter.tableId === tableId,
          ) ?? []
        const filterCount = columnFilters.length
        const sort = find(sorts, (s) => s.columnId === SystemGeneratedTableColumnType.UPDATED_BY)
        const sortDirection = sort?.direction
        return (
          <Box
            className="non-hover-content"
            sx={{
              ...styles.headerCell,
              ...styles.headerCell.leftIndent(tableColumnHeaders.length + 1),
            }}
          >
            <Box sx={styles.headerCell.icon}>
              <RiUserLine />
            </Box>
            <Box sx={styles.headerCell.secondaryName}>Updated by</Box>
            {sortDirection && (
              <Box sx={styles.headerCell.sortIcon}>
                {sortDirection === SortDirection.ASC ? <RxArrowUp /> : <RxArrowDown />}
              </Box>
            )}
            {filterCount > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  onFilterIconClick?.(SystemGeneratedTableColumnType.UPDATED_BY)
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  ml: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                <RiFilter2Fill size={16} style={{ color: 'inherit' }} />
                <Box
                  sx={{
                    visibility: filterCount > 1 ? 'visible' : 'hidden',
                    minWidth: 14,
                    height: 14,
                    color: 'primary.main',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: -1,
                    marginInlineStart: -1,
                  }}
                >
                  {filterCount}
                </Box>
              </Box>
            )}
          </Box>
        )
      },
      valueGetter: (params: { row: DetailedTableRow }) => {
        return params.row.updatedBy
          ? `${params.row.updatedBy.firstName} ${params.row.updatedBy.lastName}`
          : ''
      },
      renderCell: (params: { row: DetailedTableRow }) => {
        // Don't render for aggregation row
        if (showAggregationRow && params.row.id === AGGREGATION_ROW_ID) {
          return null
        }

        if (!params.row.updatedBy) {
          return null
        }

        const user = params.row.updatedBy

        return <UserChip user={user} />
      },
    },
  ]
}

type SimpleCell = {
  userVisibleValue?: string | null
  column: {
    id: string
    type: DataType
    order: number
    indexPrefix: string
    indexSuffix: string
  }
}

/**
 * Gets the name of a row based on the cells. Follows priority:
 * 1. ID column (always takes precedence)
 * 2. STRING columns (lowest order wins if multiple)
 * 3. Any column with non-empty value (lowest order wins if multiple)
 * Only considers cells with non-empty userVisibleValue.
 */
export function getRowName(cells: SimpleCell[]): string | null {
  // Track potential name cells with priority levels
  let idCell: SimpleCell | undefined
  let textCell: SimpleCell | undefined
  let anyCell: SimpleCell | undefined

  for (const cell of cells) {
    // Only consider cells with non-empty values
    const hasValue = cell.userVisibleValue && cell.userVisibleValue.trim() !== ''
    if (!hasValue) {
      continue
    }

    // Priority 1: ID column (always takes precedence)
    if (cell.column.type === DataType.ID) {
      idCell = cell
    }
    // Priority 2: String columns (only if no ID cell found yet)
    else if (cell.column.type === DataType.STRING) {
      if (!textCell || cell.column.order < textCell.column.order) {
        textCell = cell
      }
    }
    // Priority 3: Any column with value (only if no ID or text cell found)
    else if (!idCell && !textCell) {
      if (!anyCell || cell.column.order < anyCell.column.order) {
        anyCell = cell
      }
    }
  }

  // Return the best name cell value based on priority
  const nameCell = idCell || textCell || anyCell
  return nameCell?.userVisibleValue ?? null
}

/**
 * Gets the ID of the column that represents the name of the row
 */
export function getRowNameColumnId(
  columns: {
    id: string
    type: DataType
    order: number
  }[],
): string | null {
  const idColumn = columns.find((column) => column.type === DataType.ID)
  if (idColumn) {
    return idColumn.id
  }
  const firstColumn = first(orderBy(columns, (column) => column.order, 'asc'))
  if (firstColumn) {
    return firstColumn.id
  }

  return null
}

/**
 * Given a table tab and the current grid sort model, checks if the sort has changed.
 * Supports multiple sorts.
 */
export function getHasChangedSort(tableTab: DetailedTableTab, sortModel: GridSortModel): boolean {
  if (sortModel.length !== tableTab.sorts.length) {
    return true
  }

  return some(sortModel, (sortItem, index) => {
    const savedSort = tableTab.sorts[index]
    const savedFieldId =
      savedSort.tableColumn?.id ??
      (savedSort as { systemGeneratedColumnType?: string }).systemGeneratedColumnType

    const direction = sortItem.sort === 'asc' ? SortDirection.ASC : SortDirection.DESC

    // Check if either the column or direction is different
    return sortItem.field !== savedFieldId || direction !== savedSort.direction
  })
}

/**
 * Given a table tab and the current grid filter model, checks if the filter has changed;
 * If a user does not have permission to view a column in a filter, it will not be included in the comparison.
 * Pass tableType to include system-generated column filters in the comparison.
 */
export function getHasChangedFilters(
  tableColumns: DetailedTableColumn[],
  tableTab: DetailedTableTab,
  filterModel: DossGridFilterModel,
  tableId?: string,
  permissionsManager?: UserPermissionsManager,
): boolean {
  const viewableGridFilterModelItems = filterModel.items.filter((filterItem) => {
    if (!tableId || !permissionsManager) {
      return true
    }
    if (parseSystemColumnFieldId(filterItem.field)) {
      return true // System columns are viewable
    }
    return permissionsManager.canViewTableColumn(tableId, filterItem.field)
  })

  const viewableSavedTableTabFilters = tableTab.filters.filter((filter) => {
    if (!tableId || !permissionsManager) {
      return true
    }
    if (filter.systemGeneratedColumnType != null) {
      return true // System column filters are viewable
    }
    if (!filter.tableColumn?.id) {
      return true
    }
    return permissionsManager.canViewTableColumn(tableId, filter.tableColumn.id)
  })

  if (viewableGridFilterModelItems.length === 0 && viewableSavedTableTabFilters.length === 0) {
    return false
  }

  const hasChangedFilterCount =
    viewableGridFilterModelItems.length !== viewableSavedTableTabFilters.length
  const hasNoOperator = !filterModel.logicOperator && !tableTab.booleanOperator
  const hasChangedOperator =
    !hasNoOperator &&
    filterOperatorToBooleanOperator(filterModel.logicOperator) !== tableTab.booleanOperator
  if (hasChangedFilterCount || hasChangedOperator) {
    return true
  }

  const areAllFiltersTheSame = every(viewableGridFilterModelItems, (filterItem) => {
    const column = find(tableColumns, (tableColumn) => tableColumn.id === filterItem.field)
    if (column) {
      const filterItemAsTabFilterInput = generateFilterInput(column, filterItem)
      return some(viewableSavedTableTabFilters, (tabFilter) =>
        isFilterSame(filterItemAsTabFilterInput, tabFilter),
      )
    }

    const systemType = parseSystemColumnFieldId(filterItem.field)
    if (systemType !== null) {
      const filterItemAsTabFilterInput = generateSystemColumnFilterInput(
        systemType as SystemGeneratedTableColumnType,
        filterItem,
      )
      return some(viewableSavedTableTabFilters, (tabFilter) =>
        isFilterSame(filterItemAsTabFilterInput, tabFilter),
      )
    }

    return false
  })
  return !areAllFiltersTheSame
}

/**
 * Given the current column visibility model and the original one (saved on tab), checks if it has changed.
 * Compares both directions so changes to table columns and system-generated columns are detected.
 */
export function getHasChangedVisibleColumns(
  originalVisibilityModel: GridColumnVisibilityModel,
  currentVisibilityModel: GridColumnVisibilityModel,
): boolean {
  const allColumnIds = uniq([
    ...Object.keys(originalVisibilityModel),
    ...Object.keys(currentVisibilityModel),
  ])
  for (const columnId of allColumnIds) {
    // Match MUI: a field omitted from the model is visible (true), not hidden.
    const original = get(originalVisibilityModel, columnId, true)
    const current = get(currentVisibilityModel, columnId, true)
    if (original !== current) {
      return true
    }
  }
  return false
}

export function getNumRows(tableSource: TableSource): number {
  switch (tableSource) {
    case TableSource.CHILD_IN_RECORD_DETAIL:
    case TableSource.TABLE_DETAIL:
    case TableSource.PIVOT_TABLE_DRILLDOWN:
      return 50
    case TableSource.TABLE_IN_WORKSPACE_DASHBOARD:
    case TableSource.TABLE_IN_PROJECT_DETAIL:
      return 25
    case TableSource.CHILD_IN_TABLE_DETAIL:
      return 10
  }
}

/**
 * Evicts ALL paginatedTableRows cache entries from Apollo cache.
 * This prevents unbounded cache growth when users navigate through different
 * pages, filters, and sort combinations.
 *
 * Note: Cannot selectively evict by tableId due to Apollo's cache.evict() limitations.
 * Evicts all paginated table row queries globally.
 *
 * @param cache - Apollo cache instance
 *
 * @example
 * // Evict all cached pages when filters/sorts change
 * clearAllTableRowsFromCache(client.cache)
 *
 * @example
 * // Evict on table unmount to prevent memory leaks
 * clearAllTableRowsFromCache(client.cache)
 */
export const clearAllTableRowsFromCache = <T extends object>(cache: ApolloCache<T>) => {
  cache.evict({ fieldName: 'paginatedTableRows' })
  cache.gc()
}

/**
 * Handles single-click cell editing for DataGrid.
 * When a cell is clicked, it switches to edit mode and reverts all other cells to view mode.
 *
 * @see https://mui.com/x/react-data-grid/recipes-editing/#single-click-editing
 */
export const handleDataGridCellClick = (
  params: GridCellParams,
  event: MuiEvent<React.MouseEvent<HTMLElement>>,
  setCellModesModel: React.Dispatch<
    React.SetStateAction<Record<string | number, Record<string, { mode: GridCellModes }>>>
  >,
) => {
  if (!params.isEditable) {
    return
  }

  try {
    // Ignore portal. Have to cast the event type because typescript doesn't realize it is an HTMLElement
    const eventTarget = event.target as HTMLElement
    if (eventTarget.nodeType === 1 && !event.currentTarget.contains(eventTarget)) {
      return
    }
  } catch (error) {
    console.error('DataGrid onCellClick error:', error.message)
    return
  }

  setCellModesModel((prevModel) => {
    return {
      // Revert the mode of the other cells from other rows
      ...Object.keys(prevModel).reduce(
        (acc, id) => ({
          ...acc,
          [id]: Object.keys(prevModel[id]).reduce(
            (acc2, field) => ({
              ...acc2,
              [field]: { mode: GridCellModes.View },
            }),
            {},
          ),
        }),
        {},
      ),
      [params.id]: {
        // Revert the mode of other cells in the same row
        ...Object.keys(get(prevModel, params.id, {})).reduce(
          (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
          {},
        ),
        [params.field]: { mode: GridCellModes.Edit },
      },
    }
  })
}
